import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const TermsConditions = () => {
  const accentColor = '#8b5cf6';
  const lastUpdated = 'January 22, 2026';

  return (
    <LegalLayout app="Prismatica" docType="Terms & Conditions" icon={FileText} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Agreement to Terms
              </h2>
              <p className="mb-4 leading-relaxed">
                By downloading, installing, or using Prismatica ("the App"), you agree to be bound
                by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do
                not use the App.
              </p>
              <p className="leading-relaxed">
                These Terms constitute a legally binding agreement between you and Feral Techlab
                ("we," "our," or "us") regarding your use of the App.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Description of Service
              </h2>
              <p className="mb-4 leading-relaxed">
                Prismatica is a professional camera application that provides:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Manual camera controls including focus, exposure, ISO, and white balance</li>
                <li>Multiple shooting modes for photos and videos</li>
                <li>Real-time filters and effects</li>
                <li>Customizable floating panels for quick access to controls</li>
                <li>Support for multiple capture formats (JPEG, HEIC, PNG, DNG, MOV, MP4)</li>
                <li>Performance monitoring tools</li>
                <li>Grid overlays for composition assistance</li>
                <li>Pinch-to-zoom and tap-to-focus functionality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                License Grant
              </h2>
              <p className="mb-4 leading-relaxed">
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
                non-transferable, revocable license to download, install, and use the App for your
                personal, non-commercial purposes on any Apple-branded device that you own or control.
              </p>
              <p className="leading-relaxed">
                This license does not allow you to use the App on any device that you do not own
                or control, and you may not distribute or make the App available over a network
                where it could be used by multiple devices at the same time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Device Requirements
              </h2>
              <p className="mb-4 leading-relaxed">
                Prismatica requires:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>An iPhone or iPad with iOS 16.0 or later</li>
                <li>A device with a camera capable of video capture</li>
                <li>Sufficient storage space for captured media</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                Some advanced features may require newer device hardware or specific camera capabilities.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                User Responsibilities
              </h2>
              <p className="mb-4 leading-relaxed">
                You are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The photos and videos you capture using the App</li>
                <li>Ensuring you have the right to photograph subjects and locations</li>
                <li>Complying with all applicable laws regarding photography and recording</li>
                <li>Respecting the privacy of others when using the camera</li>
                <li>Managing your device storage for captured media</li>
                <li>Backing up important photos and videos</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Restrictions
              </h2>
              <p className="mb-4 leading-relaxed">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Copy, modify, or distribute the App or any portion thereof</li>
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Rent, lease, lend, sell, or sublicense the App</li>
                <li>Use the App to capture illegal, harmful, or unauthorized content</li>
                <li>Use the App in violation of any applicable laws or regulations</li>
                <li>Remove, alter, or obscure any proprietary notices on the App</li>
                <li>Use the App for surveillance or recording without proper consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Pro Features & In-App Purchases
              </h2>
              <p className="mb-4 leading-relaxed">
                Prismatica may offer optional Pro features through in-app purchases. If applicable:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All purchases are processed through the Apple App Store</li>
                <li>By making a purchase, you agree to Apple's Terms and Conditions for in-app purchases</li>
                <li>Subscriptions auto-renew unless cancelled at least 24 hours before the current period ends</li>
                <li>Manage subscriptions through your Apple ID account settings</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Refunds:</strong> All refund requests
                must be directed to Apple through the App Store, as we do not process payments directly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Content Ownership
              </h2>
              <p className="leading-relaxed">
                You retain full ownership of all photos and videos you capture using Prismatica.
                We do not claim any rights to your content. The App simply provides the tools
                for you to create your own media.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Intellectual Property
              </h2>
              <p className="leading-relaxed">
                The App, including all content, features, and functionality, is owned by Feral
                Techlab and is protected by international copyright, trademark, patent, trade
                secret, and other intellectual property laws. Our trademarks may not be used
                without our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Media Loss Disclaimer
              </h2>
              <p className="leading-relaxed">
                While we strive to provide reliable capture functionality, we cannot guarantee
                against media loss. You acknowledge that photos or videos may be lost due to
                device failure, iOS issues, storage limitations, or other unforeseen circumstances.
                We strongly recommend backing up important captures to iCloud Photos or another
                backup service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Disclaimer of Warranties
              </h2>
              <p className="mb-4 leading-relaxed">
                THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY
                KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
                OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="leading-relaxed">
                We do not warrant that the App will be uninterrupted, error-free, or that the
                camera features will work identically on all devices. Camera capabilities vary
                by device hardware.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Limitation of Liability
              </h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL FERAL
                TECHLAB BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
                PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, USE, GOODWILL, OR
                OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OR INABILITY TO USE THE APP,
                INCLUDING BUT NOT LIMITED TO LOSS OF CAPTURED MEDIA, MISSED PHOTO OPPORTUNITIES,
                OR ANY MALFUNCTION OF CAMERA FEATURES.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Indemnification
              </h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless Feral Techlab and its officers,
                directors, employees, and agents from and against any claims, liabilities, damages,
                losses, and expenses, including reasonable attorneys' fees, arising out of or in
                any way connected with your use of the App, content you capture, your violation
                of these Terms, or your violation of any rights of another.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Termination
              </h2>
              <p className="leading-relaxed">
                We may terminate or suspend your access to the App immediately, without prior
                notice or liability, for any reason, including if you breach these Terms. Upon
                termination, your right to use the App will immediately cease. You may also
                terminate this agreement at any time by uninstalling the App from your device.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to Terms
              </h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms at any time. If we make material
                changes, we will notify you by updating the "Last updated" date at the top of
                these Terms. Your continued use of the App after any such changes constitutes
                your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Governing Law
              </h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of
                the jurisdiction in which Feral Techlab operates, without regard to its conflict
                of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:tanushgupta@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    tanushgupta@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/prismatica/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Prismatica Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default TermsConditions;
